import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { INVOICES_KEY, type Invoice } from './types';

type Outdated = { id: string; quoteVersionNumber: number; acceptedNumber: number; message: string };

/**
 * "Wystaw fakturę" at an accepted quote version: a draft invoice from that version, then its card. The event's draft of
 * the same version opens instead (409 `draft_exists`); a draft of a version that is no longer accepted is never opened
 * silently — the quote tab says so and offers to rebuild it from the accepted version or to delete it.
 */
export function useIssueInvoice(eventId: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [outdated, setOutdated] = useState<Outdated | null>(null);
  const done = (inv: Invoice) => {
    setOutdated(null);
    void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
    navigate(`/finanse/faktury/${inv.id}`);
  };
  const create = useMutation({
    mutationFn: ({ versionId }: { versionId: string; number: number }) => api<Invoice>(`/api/invoices/events/${eventId}/draft`, { method: 'POST', body: { versionId } }),
    onSuccess: done,
    onError: (error, asked) => {
      if (!(error instanceof ApiError) || error.code !== 'draft_exists') return;
      const current = error.current as { id: string; quoteVersionNumber?: number; outdated?: boolean };
      if (current.outdated) setOutdated({ id: current.id, quoteVersionNumber: current.quoteVersionNumber ?? 0, acceptedNumber: asked.number, message: error.message });
      else navigate(`/finanse/faktury/${current.id}`);
    },
  });
  const rebuild = useMutation({
    mutationFn: async (id: string) => {
      const draft = await api<Invoice>(`/api/invoices/${id}`);
      return api<Invoice>(`/api/invoices/${id}/rebuild`, { method: 'POST', body: { version: draft.version } });
    },
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const draft = await api<Invoice>(`/api/invoices/${id}`);
      await api<void>(`/api/invoices/${id}?version=${draft.version}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      setOutdated(null);
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
    },
  });
  const failed = [create.error, rebuild.error, remove.error].find((e) => e && !(e instanceof ApiError && e.code === 'draft_exists'));
  const error = failed ? (failed instanceof ApiError ? failed.message : 'Nie udało się utworzyć faktury') : null;
  return {
    start: (versionId: string, number: number) => create.mutate({ versionId, number }),
    pending: create.isPending || rebuild.isPending || remove.isPending,
    error,
    outdated,
    rebuild: (id: string) => rebuild.mutate(id),
    remove: (id: string) => remove.mutate(id),
    open: (id: string) => navigate(`/finanse/faktury/${id}`),
  };
}

/** The error of "Wystaw fakturę" (e.g. the event is invoiced in full), or the outdated draft with what to do with it. */
export function IssueInvoiceError({ invoicing }: { invoicing: ReturnType<typeof useIssueInvoice> }) {
  const { outdated, error } = invoicing;
  if (outdated) {
    return (
      <div className="alert q-notice" role="alert">
        {outdated.message}{' '}
        <button className="btn small primary" type="button" disabled={invoicing.pending} onClick={() => invoicing.rebuild(outdated.id)}>
          Przebuduj z v{outdated.acceptedNumber}
        </button>{' '}
        <button className="btn small" type="button" disabled={invoicing.pending} onClick={() => invoicing.open(outdated.id)}>
          Otwórz roboczą v{outdated.quoteVersionNumber}
        </button>{' '}
        <button className="btn small" type="button" disabled={invoicing.pending} onClick={() => invoicing.remove(outdated.id)}>
          Usuń roboczą
        </button>
      </div>
    );
  }
  return error ? (
    <p className="alert" role="alert">
      {error}
    </p>
  ) : null;
}
